<template>
  <div>

    <div class="card mt-2" qid="basic-info-section">
      <div class="card-body pr-0 pl-0">

        <div class="content-view-body">

          <span class="btn btn-edit float-right" @click="showFormFunction(process)" v-if="!showForm">
            <font-awesome-icon icon="pen"/>
          </span>

          <b-form @submit="formSubmit" qid="create-p-form" v-if="showForm">
            <b-alert v-if="error.length"
                     show
                     variant="danger"
                     qid="create-p-error"
            >
              {{ error }}
            </b-alert>
            <div class="form-container">
              <b-form-group>
                <label qid="create-p-name-label">
                  {{ $t('processes.create.name_label') }}
                </label>
                <b-form-input
                  qid="create-p-form-name"
                  type="text"
                  v-model="pForm.name"
                  :maxLength="255"
                  required
                  :placeholder="$t('processes.create.name_placeholder')"
                ></b-form-input>
              </b-form-group>
              <b-form-group>
                <label qid="create-do-tags-label">
                  {{ $t('processes.create.tags_label') }}
                </label>
                <multiselect
                    v-model="selectedTags"
                    :options="tags"
                    :multiple="true"
                    :taggable="true"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    @search-change="loadTags"
                    track-by="name_translated"
                    :placeholder="$t('processes.create.tags_placeholder')"
                    label="name_translated"
                    qid="create-do-tags-select"
                    @tag="addTag"
                ></multiselect>
              </b-form-group>
              <b-form-group>
                <label qid="create-p-users-label">
                  {{ $t('processes.create.owners_label') }}
                </label>
                <multiselect
                    v-model="selectedUsers"
                    :options="users"
                    :multiple="true"
                    :taggable="false"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    @search-change="loadUsers"
                    @remove="removeUserFromDropdown"
                    track-by="slug"
                    :placeholder="$t('processes.create.owners_placeholder')"
                    label="name"
                    qid="create-p-users-select"
                >
                  <template slot="tag" slot-scope="props">
                    <span></span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">
                      {{ props.option.name }}
                    </span>
                  </template>
                </multiselect>
                <div class="form-control auto-height mt-1" v-if="selectedUsers && selectedUsers.length">
                  <div v-for="(user, index) in selectedUsers" class="mt-1 mb-1">
                    <div class="row">
                      <div class="col-sm-1">
                        <font-awesome-icon
                          icon="star"
                          :class="{'color-yellow': user.owner, 'text-light': !user.owner}"
                          class="cursor-pointer"
                          @click="setOwner(index)"
                        />
                      </div>
                      <div class="col-sm-10">
                        <Avatar :object="user" size="xs" :noOwner="true"></Avatar>
                        <span class="ml-2">{{ user.name }}</span>
                      </div>
                      <div class="col-sm-1">
                        <font-awesome-icon
                          icon="times"
                          class="cursor-pointer"
                          @click="removeUser(index)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <span class="text-light">
                  * {{ $t('processes.create.owners_tip') }}
                </span>
              </b-form-group>
              <b-form-group>
                <label qid="create-p-pg-label">
                  {{ $t('processes.create.process_group_label') }}
                </label>
                <multiselect
                    v-model="processGroup"
                    :options="processGroups"
                    @search-change="loadProcessGroups"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    :placeholder="$t('processes.create.process_group_placeholder')"
                    label="name"
                    track-by="slug"
                    qid="create-p-form-pg"
                ></multiselect>
              </b-form-group>
            </div>
            <div class="text-right">
              <b-button class="mt-3"
                        variant="secondary"
                        @click="hideFormFunction"
                        qid="create-p-cancel-button"
              >
                {{ $t('processes.create.cancel') }}
              </b-button>
              <b-button type="submit"
                        class="mt-3 ml-3"
                        variant="success"
                        :disabled="buttonDisabled"
                        qid="create-p-submit-button"
              >
                <span v-if="buttonDisabled" class="mr-1">
                  <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                </span>
                {{ $t('processes.create.submit') }}
              </b-button>
            </div>
          </b-form>

          <div v-if="!showForm">
            <label class="mt-3" v-if="process && process.name">
              {{ $t('systems.create.name_label') }}
            </label>
            <br>
            <span v-if="process && process.name">
              {{ process.name }}
            </span>
            <br>

            <label class="mt-3" v-if="process && process.name">
              {{ $t('systems.create.tags_label') }}
            </label>
            <br>
            <span class="badge mb-1 mr-1" v-for="tag in process.tags_translated" :title="tag.name_translated">
              <font-awesome-icon icon="tag"/>
              {{ tag.name_translated | truncate(35) }}
            </span>
            <br>

            <label class="mt-3" v-if="process">
              {{ $t('systems.create.owners_label') }}
            </label>
            <br>
            <span class="mr-1" v-for="user in process.users" qid="process-users-list">
              <Avatar :object="user" size="xs"></Avatar>
            </span>
            <br>

            <label class="mt-3" v-if="process && process.process_group">
              {{ $t('processes.create.process_group_label') }}
            </label>
            <br>
            <span class="mr-1" qid="process-countries-list" v-if="process && process.process_group">
              <span class="badge mb-1">
                <font-awesome-icon icon="project-diagram"/>
                {{ process.process_group.name }}
              </span>
            </span>
            <br>
          </div>

        </div>
      </div>
    </div>
  </div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';

  export default {
    name: 'BasicInfoTaskBlock',
    data: function() {
      return {
        pForm: {
          name: '',
          users: '',
          tags: [],
          users: [],
          process_group_id: ''
        },
        selectedTags: [],
        error: '',
        selectedUsers: [],
        processGroup: [],
        loadingItems: false,
        users: [],
        tags: [],
        processGroups: [],
        newTags: [],
        editParam: false,
        modalShown: false,
        buttonDisabled: false,
        showForm: true,
        myTasks: [],
        process: {},
        myGuides: []
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      },
      edit: {
        type: Boolean,
      },
      task: {},
      submitForm: {}
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getReloadContent() {
        return this.$store.state.updateContent
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadUsers();
        this.loadTags();
      },
      object: function (value) {
        let self = this;
        if (value && value.id) {
          this.process = value
        }
      },
      submitForm: function (value) {
        if (value) {
          if (this.showForm) {
            this.formSubmit()
          }
        }
      }
    },
    methods: {
      addTag(newTag) {
        const tag = {
          name: newTag,
          translatable: newTag,
          name_translated: newTag
        }
        this.selectedTags.push(tag)
        this.newTags.push(tag)
      },
      removeUser(index) {
        let selectedItem = this.selectedUsers[index]
        selectedItem.owner = false;
        this.$set(this.selectedUsers, index, selectedItem)
        // Remove item from selected users array
        this.selectedUsers.splice(index, 1);
      },
      removeUserFromDropdown(item) {
        item.owner = false;
      },
      setOwner(index) {
        let selectedItem = this.selectedUsers[index]
        _.forEach(this.selectedUsers, function (item) {
          item.owner = false;
        })
        selectedItem.owner = true;
        this.$set(this.selectedUsers, index, selectedItem)
      },
      showFormFunction(object) {
        let self = this;
        this.error = ''
        this.selectedTags = []
        this.selectedUsers = []
        this.showForm = true;

        this.loadUsers();
        this.loadTags();

        this.processGroup = object.process_group;

        if (object.users && object.users.length) {
          self.selectedUsers = [];
          _.forEach(object.users, function (user) {
            let matchedUser = _.cloneDeep(user)
            if (user.id == object.owner) {
              matchedUser.owner = true
            }
            self.selectedUsers.push(matchedUser)
          })
        }

        if (object.tags_translated && object.tags_translated.length) {
          this.selectedTags = _.cloneDeep(object.tags_translated);
        }

        this.pForm.slug = object.slug;
        this.pForm.name = object.name;
      },
      hideFormFunction() {
        this.newTags = [];
        this.showForm = false;
      },
      navigateToTask(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
        this.$router.push({ name: 'processes.view', params: { slug: this.process.slug }, query: { task: object.id }})
      },
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      loadTasks(code) {
        let self = this;
        if (this.getClient.projectModule) {
          piincHttp.get('projects/me/tasks', { params:
            {
              per_page: 1,
              status: 'pending',
              model_type: this.process.who_am_i,
              model_id: this.process.id,
              code: code,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.myTasks = []
            if (response.data.items.length) {
              self.myTasks.push(response.data.items[0])
            }
          }, function() {});
        }
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Process' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Process',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      formSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        let selectedOwner = false;

        if (evt) {
          evt.preventDefault();
        }

        this.pForm.tags = [];
        if (this.selectedTags && this.selectedTags.length) {
          for (var i = 0; i < this.selectedTags.length; i++) {
            this.pForm.tags.push(this.selectedTags[i].name_translated);
          }
        }

        this.pForm.users = [];
        if (this.selectedUsers && this.selectedUsers.length) {
          for (var i = 0; i < this.selectedUsers.length; i++) {
            this.pForm.users.push(this.selectedUsers[i].id);
            if (this.selectedUsers[i].owner) {
              selectedOwner = true;
              this.pForm.owner = this.selectedUsers[i].id;
            }
          }
        } else {
          selectedOwner = true;
        }

        if (this.processGroup) {
          this.pForm.process_group_id = this.processGroup.id;
        } else {
          this.pForm.process_group_id = ''
        }

        if (selectedOwner) {
          piincHttp.put('processes/' + this.pForm.slug, this.pForm).then(function(response) {
            self.buttonDisabled = false;
            self.hideFormFunction();
            self.process = response.data;
            self.$store.commit('storeUpdateContent', self.process);
            self.$emit('reloadContent', response.data);
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
            }
          })
        } else {
          this.error = this.$t('system.owners_error_message')
          this.buttonDisabled = false;
        }
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.tags = response.data
          if (self.tags && !self.tags.length && !query) {
            self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
          }
          for (var i = 0; i < self.newTags.length; i++) {
            self.tags.push(self.newTags[i]);
          }
        }, function() {});
      },
      loadProcessGroups(query) {
        let self = this;
        piincHttp.get('process-groups', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.processGroups = response.data.items
          self.showFormFunction(self.process)
          if (self.processGroups && !self.processGroups.length && !query) {
            self.processGroups.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadContent() {
        let self = this;
        piincHttp.get('processes/' + this.process.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.process = response.data;
        }, function() {});
      }
    },
    components: {
      Avatar
    },
    created () {
      let self = this;
      this.process = this.object
      if (this.process && this.process.slug) {
        this.loadTasks('update_basic_info');
        this.loadContent()
      }

      this.loadGuides('update_basic_info');
      if (this.getClient && this.getClient.slug) {
        this.loadProcessGroups();
      }
    }
  }
</script>
